import { Box, Container, Divider, Grid, Link, Step, StepConnector, StepLabel, Stepper, Typography, withStyles } from "@material-ui/core"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { PedidoModel } from "api/cardapio/models/pedido/pedido"
import { useParams } from "react-router-dom"
import { obterPedido } from "api/pedidos/queries"
import { EnumTipoPedido, EnumStatusPedido } from "api/cardapio/models/pedido/efetuar-pedido"
import { toDateString } from "utils/to-date"
import { PedidoValor } from "./components/pedido-valor/pedido-valor"
import { CardapioStorageKeys, useCardapioStorage } from "storage/cardapio-storage"
import { CardapioDadosProps } from "modulos/cardapio/containers/cardapio/cardapio-header/cardapio-header-props"
import { StepIcone } from "components/stepper/componentes/step-icone/step-icone"
import { enderecoFormatado } from "utils/endereco-formatado"
import { Carregando } from "components/carregando"
import { OkFilledIcon } from "components/icons/ok-filled-icon"
import { useFuncoesNavbarStore } from "services/store/funcoes-navbar"
import { useThemeQueries } from "components/dialogs/dialog-adicionar-produto-com-subitem/util-styles"
import { phoneMask } from "utils/phone-mask"
import { useAcompanharPedidoStore } from "services/store/acompanhar-pedido"
import { CancelFilledIcon } from "components/icons/cancel-filled-icon"
import { useStyles } from "./acompanhar-pedido-styles"
import { ProdutoPedidoCard } from "components/modals/resumo-pedido/components/item-mesa-card/item-mesa-card"
import { stringNumeros } from "utils/string-numeros"
import { VariaveisAmbiente } from "ambiente"
import { getIDFromUrl } from "utils/get-id-url"

const ColorlibConnector = withStyles((theme) => ({
    // Estilos globais para aplicar o anel pulsante diretamente ao container do ícone quando ativo

    alternativeLabel: {
    },
    active: {
        '& $line': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    completed: {
        '& $line': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    vertical: {
        padding: theme.spacing(.5, 0),
    },
    horizontal: {
        padding: theme.spacing(0, .5),
    },
    lineVertical: {
        height: 1,
        width: 1,
    },
    lineHorizontal: {
        height: 1,
    },
    line: {
        border: 0,
        backgroundColor: '#eaeaf0',
        borderRadius: 1,
    },
}))(StepConnector);

export const AcompanharPedido = () => {
    const classes = useStyles()
    const { empresaId } = useParams<{ empresaId: string }>()
    const { removerPedido } = useAcompanharPedidoStore((state) => state)
    const { setTitulo } = useFuncoesNavbarStore()
    const { getRegistro } = useCardapioStorage()
    const dadosGerais = getRegistro(CardapioStorageKeys.DadosGerais, false) as CardapioDadosProps;
    const configDelivery = dadosGerais.configuracoesDelivery
    const empresaAtual = dadosGerais.empresa
    const { isMobile } = useThemeQueries()
    const pedidoId = getIDFromUrl('pedidoId')

    const [pedidoAtual, setPedidoAtual] = useState<PedidoModel>()
    const [carregando, setCarregando] = useState(false)
    const [activeStep, setActiveStep] = useState(0)
    const intervalRef = useRef<number | null>(null)
    const firstTimeLoading = useRef(true)

    const produtoEntregaId = configDelivery?.produtoEntrega?.id

    const getPedidoHandle = useCallback(async () => {
        try {
            setCarregando(true)
            const res: PedidoModel = await obterPedido.execute({ empresaId: empresaId, pedidoId: pedidoId });

            setPedidoAtual(res)
            const newStep = (() => {
                if ([EnumStatusPedido.FECHADO, EnumStatusPedido.FINALIZADOOUENTREGUE, EnumStatusPedido.IMPORTADO].includes(res.statusPedido.codigo)) {
                    setTitulo(`Detalhes pedido #${res.codigoPedido}`)
                    return 4
                } else {
                    setTitulo(`Acompanhar pedido #${res.codigoPedido}`)
                    return res.statusPedido.codigo - 1
                }
            })()
            setActiveStep(newStep)
            firstTimeLoading.current = false
        } catch (err: any) {
            console.log(err)
        } finally {
            setCarregando(false)
        }
    }, [empresaId, pedidoId, setTitulo])

    useEffect(() => {
        // primeira execução imediata
        getPedidoHandle()

        // inicia o intervalo de 15s para buscar atualizações
        intervalRef.current = window.setInterval(() => {
            getPedidoHandle()
        }, 15000)

        // cleanup ao desmontar ou reexecutar efeito
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [getPedidoHandle])

    // encerra a busca quando o pedido for finalizado
    useEffect(() => {
        if (pedidoAtual?.statusPedido?.codigo === EnumStatusPedido.FINALIZADOOUENTREGUE) {
            if (intervalRef.current) {
                removerPedido()
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }

        return () => {
            setTitulo('')
        }
    }, [pedidoAtual?.statusPedido?.codigo, removerPedido, setTitulo])

    const produtos = useMemo(() =>
        (pedidoAtual?.produtos ?? [])
            .filter(produto => produto.produtoIdReferencia !== produtoEntregaId)
        , [pedidoAtual?.produtos, produtoEntregaId])

    const valorSubTotal = useMemo(() => produtos.filter(produto => produto.produtoIdReferencia !== produtoEntregaId).reduce((acc, produto) => acc + produto.valorTotal, 0), [produtos, produtoEntregaId])


    const isEntrega = pedidoAtual?.tipoPedido.codigo === EnumTipoPedido.ENTREGA
    const steps = [
        'Aguardando Confirmação',
        'Confirmado',
        'Em Preparo',
        isEntrega ? 'Em rota de entrega' : 'Pronto para retirar',
        'Pedido ' + (isEntrega ? 'Entregue' : 'Recebido') + '!',
    ]

    const pedidoAgrupado = () => {
        return produtos.filter(x => !x.produtoPai).map((x) => ({
            ...x,
            subItens: produtos.filter(y => y.groupId === x.codigoReferencia && x.produtoIdReferencia !== y.produtoIdReferencia)
        }))
    }

    const pedidoAtualEndereco = pedidoAtual?.enderecoEntrega

    const redirectToWhatsapp = useCallback(() => {
        const numero = stringNumeros(empresaAtual?.fone || '');
        if (!numero) return;
        const url = VariaveisAmbiente.whatsAppLink
            .replace('@telefone', `55${numero}`)
            .replace('@mensagem', '')
            .replace(' ', '%20');
        return url;
    }, [empresaAtual?.fone]);

    if (!pedidoAtual || (carregando && firstTimeLoading.current)) return (
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            {carregando && (
                <Carregando
                    carregando
                    titulo='Carregando pedido'
                    mensagem={'Aguarde enquanto carregamos os dados do seu pedido'}
                />
            )}
        </Box>
    )


    return (
        <Container fixed maxWidth="md" className={classes.container}>
            <Box p={2} className={classes.root}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <Typography align="center">
                                <Box fontWeight={700}>
                                    {pedidoAtual.statusPedido.codigo !== EnumStatusPedido.FINALIZADOOUENTREGUE ? 'Acompanhar ' : ''}Pedido N° {pedidoAtual.codigoPedido}
                                </Box>
                            </Typography>
                            <Typography color="textSecondary" variant="caption" align="center">
                                Feito em: {toDateString(pedidoAtual.dataCriacao, 'DD/MM/YYYY HH:mm')}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    {pedidoAtual.statusPedido.codigo === EnumStatusPedido.FINALIZADOOUENTREGUE ? (
                        <Grid item xs={12}>
                            <Box display="flex" flexDirection="column" alignItems="center">
                                <OkFilledIcon tipo="GERAL" class={classes.finalizadoIcon} />
                                <Typography align="center" variant="h6">
                                    <Box fontWeight={700}>
                                        Pedido Finalizado!
                                    </Box>
                                </Typography>
                                <Typography variant="body2" color="textSecondary" align="center">
                                    Agradecemos pela preferência e esperamos que sua experiência tenha sido excelente!
                                </Typography>
                            </Box>
                        </Grid>
                    ) : pedidoAtual.statusPedido.codigo === EnumStatusPedido.CANCELADO ? (
                        <Grid item xs={12}>
                            <Box display="flex" flexDirection="column" alignItems="center">
                                <CancelFilledIcon tipo="GERAL" class={classes.finalizadoIcon} />
                                <Typography align="center" variant="h6">
                                    <Box fontWeight={700}>
                                        Pedido Cancelado!
                                    </Box>
                                </Typography>
                                <Typography variant="body2" color="textSecondary" align="center">
                                    O pedido foi cancelado. Se você tiver alguma dúvida, por favor, entre em contato conosco.
                                </Typography>
                                <Typography variant="body1" color="textPrimary" align="center">
                                    <Box fontWeight={600}>
                                        {phoneMask(empresaAtual?.fone || '')}
                                    </Box>
                                </Typography>
                            </Box>
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <Stepper
                                className={classes.stepper}
                                activeStep={activeStep}
                                orientation={isMobile ? "vertical" : "horizontal"}
                                alternativeLabel={isMobile === false}
                                connector={<ColorlibConnector />}
                            >
                                {steps.map((step) => (
                                    <Step key={step}>
                                        <StepLabel
                                            StepIconComponent={(props) => StepIcone(props)}
                                        >
                                            {step}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    {pedidoAtual.tipoPedido.codigo === EnumTipoPedido.ENTREGA ? (
                        <Grid item xs={12} className={classes.gridItem}>
                            <Typography variant="caption" color="textSecondary">
                                <Box fontWeight={600} mb={2}>
                                    Endereço de Entrega
                                </Box>
                            </Typography>
                            <Typography variant="body2" color="textPrimary">
                                {enderecoFormatado(
                                    pedidoAtualEndereco?.logradouro || '',
                                    pedidoAtualEndereco?.numero || '',
                                    pedidoAtualEndereco?.bairro || '',
                                    pedidoAtualEndereco?.municipio || '',
                                    pedidoAtualEndereco?.uf || '',
                                    pedidoAtualEndereco?.complemento || '',
                                    pedidoAtualEndereco?.cep || '',
                                    pedidoAtualEndereco?.pontoReferencia || '',
                                    false
                                )}
                            </Typography>
                        </Grid>
                    ) : (
                        <Grid item xs={12} className={classes.gridItem}>
                            <Typography variant="caption" color="textSecondary">
                                <Box fontWeight={600} mb={2}>
                                    Endereço de Retirada
                                </Box>
                            </Typography>
                            <Typography variant="body2" color="textPrimary">
                                {enderecoFormatado(
                                    empresaAtual?.logradouro || '',
                                    empresaAtual?.numero || '',
                                    empresaAtual?.bairro || '',
                                    empresaAtual?.municipio || '',
                                    empresaAtual?.uf || '',
                                    empresaAtual?.complemento || '',
                                    empresaAtual?.cep || '',
                                    '',
                                    false
                                )}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <PedidoValor
                            valorTotalPedido={pedidoAtual.valorTotalPedido}
                            valorTotalServico={pedidoAtual.valorTotalServico}
                            valorTotalFrete={pedidoAtual.valorTotalFrete}
                            valorTotalAcrescimo={pedidoAtual.valorTotalAcrescimo}
                            valorTotalDesconto={pedidoAtual.valorTotalDesconto}
                            valorSubTotal={valorSubTotal}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">
                            <Box fontWeight={600} mb={2}>
                                Itens do Pedido
                            </Box>
                        </Typography>
                        {pedidoAgrupado().map((item) => (
                            <ProdutoPedidoCard key={item.id} item={item} />
                        ))}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="center">
                            Problemas com o pedido? Ligue para o número:
                        </Typography>
                        <Typography align="center" className={classes.pointer}>
                            <Link href={redirectToWhatsapp()} target="_blank">
                                {phoneMask(empresaAtual?.fone || '')}
                            </Link>
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    )
}